/*
 * LayoutContext is a context created for managing page layout related state.

 * It's now only used in OO for different bottom navigation components to set the bottomNavHeight 
 * so SuccessfulUpsellAddSnackbar can position itself correctly above them.
  
 * Usage:
 * 1. <LayoutProvider> is used as a root wrapper in App.js in bento-ordering.
 * 2. It has to be at the root level bc mobile bottom navigation is in CartPageLayout component
 *    and desktop bottom navigation is in CartRouteContainer component
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const LayoutContext = React.createContext({
  bottomNavHeight: 0,
  setBottomNavHeight: () => {},
  bottomNavComponentRef: null
});

function LayoutProvider({ children }) {
  const [bottomNavHeight, setBottomNavHeight] = useState(0);
  const bottomNavComponentRef = useRef(null);

  const value = useMemo(
    () => ({
      bottomNavHeight,
      setBottomNavHeight,
      bottomNavComponentRef
    }),
    [bottomNavHeight, bottomNavComponentRef]
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}

LayoutProvider.propTypes = {
  children: PropTypes.node.isRequired
};

function useLayout() {
  const context = React.useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }

  const { setBottomNavHeight, bottomNavComponentRef } = context;

  useEffect(() => {
    if (bottomNavComponentRef?.current) {
      const resizeObserver = new ResizeObserver(entries => {
        const navHeight = entries[0].target.offsetHeight;
        setBottomNavHeight(navHeight);
      });

      resizeObserver.observe(bottomNavComponentRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [bottomNavComponentRef?.current]);

  return context;
}

export { LayoutContext, LayoutProvider, useLayout };
